/**
 * The one phone number rule, as the form applies it before anything is sent (D-151 rule 3). It
 * mirrors the server's PhoneNumberValidation.Normalize (server/src/Modules/AGWA.Auth/Jobs/
 * PhoneNumberValidation.cs), which is the gate of record: this copy exists so a person sees the
 * refusal at the field instead of after a round trip, and its sentences are the server's word for
 * word so the two can never read differently. A change to either side changes both.
 */

/** The longest number kept, counted after trimming. The column is nvarchar(30). */
export const PHONE_NUMBER_MAX_LENGTH = 30;

export const PHONE_NUMBER_TOO_LONG =
  'Phone number must be at most 30 characters.';
export const PHONE_NUMBER_BAD_CHARACTERS =
  'Phone number may contain only digits, spaces, +, (, ) and -.';
export const PHONE_NUMBER_NO_DIGIT =
  'Phone number must contain at least one digit.';

const ALLOWED = /^[0-9 +()-]+$/;

/**
 * The number as it will be stored, or the refusal. Leading and trailing spaces are removed; what
 * is left empty means no number (value null, no error). Otherwise the value is kept as typed.
 * Checked in the server's order - length, then characters, then a digit - so an over-long number
 * is refused for its length whatever it contains, as it is there.
 */
export function normalizePhoneNumber(raw: string | null | undefined): {
  value: string | null;
  error: string | null;
} {
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') return { value: null, error: null };
  if (trimmed.length > PHONE_NUMBER_MAX_LENGTH)
    return { value: null, error: PHONE_NUMBER_TOO_LONG };
  if (!ALLOWED.test(trimmed))
    return { value: null, error: PHONE_NUMBER_BAD_CHARACTERS };
  if (!/[0-9]/.test(trimmed))
    return { value: null, error: PHONE_NUMBER_NO_DIGIT };
  return { value: trimmed, error: null };
}

/**
 * The link that calls a stored number: tel: with its digits and a leading + only (rule 3), so
 * "+61 (02) 9999-9999" calls +610299999999. A + anywhere but first is dropped with the rest of the
 * punctuation, because only a leading one means "international" to a dialler.
 */
export function phoneLinkTarget(phoneNumber: string): string {
  const trimmed = phoneNumber.trim();
  const digits = trimmed.replace(/[^0-9]/g, '');
  return `tel:${trimmed.startsWith('+') ? '+' : ''}${digits}`;
}
